import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../Constant';
import { showLoading } from 'react-global-loading';
import { toast } from 'react-toastify';

// API configuration with multiple token fallbacks
const getAuthConfig = () => {
  let token = localStorage.getItem('token') || 
               localStorage.getItem('jwt') || 
               localStorage.getItem('accessToken') || 
               localStorage.getItem('authToken') ||
               localStorage.getItem('userToken');
  
  if (!token) {
    token = sessionStorage.getItem('token') || 
            sessionStorage.getItem('jwt') || 
            sessionStorage.getItem('accessToken');
  }
  
  if (!token) {
    console.error('No token found in localStorage or sessionStorage');
    return null;
  }
  
  return {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/json',
      'Cache-Control': 'no-cache'
    }
  };
};

// Global cache for ASI Item Master data
let globalItemMasterCache = null;
let globalCacheTimestamp = null;
let isApiCallInProgress = false; // Flag to prevent multiple simultaneous API calls
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

// Global cache for Balance Sheet Block Item data
let globalBlockItemCache = null;
let globalBlockItemCacheTimestamp = null;
let globalBlockItemCacheType = null; // Store the block_type used for cache
let isBlockItemApiCallInProgress = false; // Flag to prevent multiple simultaneous API calls

const useASIItemMaster = () => {
  const location = useLocation();
  
  const [itemMasterData, setItemMasterData] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Balance Sheet Block Item Data states
  const [blockItemData, setBlockItemData] = useState([]);
  const [filteredBlockItems, setFilteredBlockItems] = useState([]);
  const [isLoadingBlockItems, setIsLoadingBlockItems] = useState(false);
  const [blockItemError, setBlockItemError] = useState(null);
  const [blockItemCurrentPage, setBlockItemCurrentPage] = useState(1);
  const [hasMoreBlockItems, setHasMoreBlockItems] = useState(true);
  const [isLoadingMoreBlockItems, setIsLoadingMoreBlockItems] = useState(false);
  const [blockItemSearchTerm, setBlockItemSearchTerm] = useState('');
  
  // Items per page for pagination
  const ITEMS_PER_PAGE = 15;
  
  // Function to determine block_type based on current route
  const getBlockType = useCallback(() => {
    const currentPath = location.pathname;
    console.log('Current path:', currentPath);
    
    // Check if current route is /balanceSheetData
    if (currentPath.includes('/balanceSheetData')) {
      console.log('Route detected: /balanceSheetData - Using block_type: D');
      return 'D';
    }
    
    // Default to 'C' for other routes
    console.log('Default route - Using block_type: C');
    return 'C';
  }, [location.pathname]);
  
  // Check if cache is valid
  const isCacheValid = useMemo(() => {
    if (!globalItemMasterCache || !globalCacheTimestamp) return false;
    return Date.now() - globalCacheTimestamp < CACHE_DURATION;
  }, []);
  
  // Check if block item cache is valid
  const isBlockItemCacheValid = useMemo(() => {
    if (!globalBlockItemCache || !globalBlockItemCacheTimestamp || !globalBlockItemCacheType) return false;
    
    const currentBlockType = getBlockType();
    // Cache is only valid if it's for the same block_type
    if (globalBlockItemCacheType !== currentBlockType) {
      console.log(`Cache invalid: stored block_type (${globalBlockItemCacheType}) != current block_type (${currentBlockType})`);
      return false;
    }
    
    return Date.now() - globalBlockItemCacheTimestamp < CACHE_DURATION;
  }, [getBlockType]);

  // Fetch ASI Item Master data from API
  const fetchItemMasterData = useCallback(async (forceRefresh = false) => {
    // Use cache if available and valid, unless force refresh is requested
    if (isCacheValid && !forceRefresh && globalItemMasterCache) {
      console.log('Using cached ASI Item Master data');
      setItemMasterData(globalItemMasterCache);
      setFilteredItems(globalItemMasterCache.slice(0, ITEMS_PER_PAGE));
      setHasMoreData(globalItemMasterCache.length > ITEMS_PER_PAGE);
      return;
    }
    
    // Prevent multiple simultaneous API calls
    if (isApiCallInProgress) {
      console.log('API call already in progress, skipping...');
      return;
    }
    
    isApiCallInProgress = true;
    setIsLoading(true);
    setError(null);
    showLoading(true);
    
    try {
      const currentConfig = getAuthConfig();
      if (!currentConfig) {
        throw new Error('Authentication token not found');
      }

      console.log('Fetching ASI Item Master data...');
      const response = await axios.get(`${API_URL}api/asi/getASIItemMasterData`, currentConfig);
      
      if (response.data.status && response.data.data) {
        const data = response.data.data;
        console.log(`Loaded ${data.length} items from ASI Item Master`);
        
        // Sort data by ITEM_NAME for consistent ordering
        const sortedData = data.sort((a, b) => 
          a.ITEM_NAME.localeCompare(b.ITEM_NAME)
        );
        
        // Store in global cache
        globalItemMasterCache = sortedData;
        globalCacheTimestamp = Date.now();
        
        // Update local state
        setItemMasterData(sortedData);
        setFilteredItems(sortedData.slice(0, ITEMS_PER_PAGE));
        setHasMoreData(sortedData.length > ITEMS_PER_PAGE);
        setCurrentPage(1);
      } else {
        throw new Error(response.data.message || 'Failed to load item master data');
      }
    } catch (error) {
      console.error('Error fetching ASI Item Master data:', error);
      setError(error.message);
      
      if (error.response?.status === 403) {
        toast.error('Access denied (403). Please check your permissions.');
      } else if (error.response?.status === 401) {
        toast.error('Unauthorized (401). Please login again.');
      } else {
          toast.error('Error loading item master data. Please try again.');
      }
    } finally {
      isApiCallInProgress = false; // Reset flag
      setIsLoading(false);
      showLoading(false);
    }
  }, []); // Remove isCacheValid dependency to prevent infinite loop
  
  // Fetch Balance Sheet Block Item Data from API
  const fetchBlockItemData = useCallback(async (forceRefresh = false) => {
    // Use cache if available and valid, unless force refresh is requested
    if (isBlockItemCacheValid && !forceRefresh && globalBlockItemCache) {
      console.log('Using cached Balance Sheet Block Item data');
      setBlockItemData(globalBlockItemCache);
      setFilteredBlockItems(globalBlockItemCache.slice(0, ITEMS_PER_PAGE));
      setHasMoreBlockItems(globalBlockItemCache.length > ITEMS_PER_PAGE);
      return;
    }
    
    // Prevent multiple simultaneous API calls
    if (isBlockItemApiCallInProgress) {
      console.log('Block Item API call already in progress, skipping...');
      return;
    }
    
    isBlockItemApiCallInProgress = true;
    setIsLoadingBlockItems(true);
    setBlockItemError(null);
    showLoading(true);
    
    try {
      const currentConfig = getAuthConfig();
      if (!currentConfig) {
        throw new Error('Authentication token not found');
      }

      const blockType = getBlockType();
      const payload = {
        block_type: blockType
      };

      console.log('Fetching Balance Sheet Block Item data with block_type:', blockType);
      const response = await axios.post(`${API_URL}api/asi/balanceSheetBlockItemData`, payload, currentConfig);

      if (response.data.status && response.data.data) {
        const data = response.data.data;
        console.log(`Loaded ${data.length} block items from API`);
        
        // Sort data by item_name for consistent ordering
        const sortedData = data.sort((a, b) => 
          (a.item_name || '').localeCompare(b.item_name || '')
        );
        
        // Store in global cache
        globalBlockItemCache = sortedData;
        globalBlockItemCacheTimestamp = Date.now();
        globalBlockItemCacheType = blockType;
        
        // Update local state
        setBlockItemData(sortedData);
        setFilteredBlockItems(sortedData.slice(0, ITEMS_PER_PAGE));
        setHasMoreBlockItems(sortedData.length > ITEMS_PER_PAGE);
        setBlockItemCurrentPage(1);
      } else {
        throw new Error(response.data.message || 'Failed to load block item data');
      }
    } catch (error) {
      console.error('Error fetching Balance Sheet Block Item data:', error);
      setBlockItemError(error.message);
      
      if (error.response?.status === 403) {
        toast.error('Access denied (403). Please check your permissions.');
      } else if (error.response?.status === 401) {
        toast.error('Unauthorized (401). Please login again.');
      } else {
        toast.error('Error loading block item data. Please try again.');
      }
    } finally {
      isBlockItemApiCallInProgress = false; // Reset flag
      setIsLoadingBlockItems(false);
      showLoading(false);
    }
  }, []); // Remove isBlockItemCacheValid dependency to prevent infinite loop

  // Load more data for pagination
  const loadMoreData = useCallback(() => {
    if (isLoadingMore || !hasMoreData) return;
    
    setIsLoadingMore(true);
    
    // Simulate loading delay to prevent UI freeze
    setTimeout(() => {
      const nextPage = currentPage + 1;
      const startIndex = (nextPage - 1) * ITEMS_PER_PAGE;
      const endIndex = startIndex + ITEMS_PER_PAGE;
      
      const newItems = itemMasterData.slice(startIndex, endIndex);
      
      if (newItems.length > 0) {
        setFilteredItems(prev => [...prev, ...newItems]);
      setCurrentPage(nextPage);
        setHasMoreData(endIndex < itemMasterData.length);
      } else {
        setHasMoreData(false);
      }
      
      setIsLoadingMore(false);
    }, 100); // Small delay to prevent UI freeze
  }, [currentPage, hasMoreData, isLoadingMore, itemMasterData]);
  
  // Load more block item data for pagination
  const loadMoreBlockItemData = useCallback(() => {
    if (isLoadingMoreBlockItems || !hasMoreBlockItems) return;
    
    setIsLoadingMoreBlockItems(true);
    
    // Simulate loading delay to prevent UI freeze
    setTimeout(() => {
      const nextPage = blockItemCurrentPage + 1;
      const startIndex = (nextPage - 1) * ITEMS_PER_PAGE;
      const endIndex = startIndex + ITEMS_PER_PAGE;
      
      const newItems = blockItemData.slice(startIndex, endIndex);
      
      if (newItems.length > 0) {
        setFilteredBlockItems(prev => [...prev, ...newItems]);
        setBlockItemCurrentPage(nextPage);
        setHasMoreBlockItems(endIndex < blockItemData.length);
      } else {
        setHasMoreBlockItems(false);
      }
      
      setIsLoadingMoreBlockItems(false);
    }, 100); // Small delay to prevent UI freeze
  }, [blockItemCurrentPage, hasMoreBlockItems, isLoadingMoreBlockItems, blockItemData]);
  
  // Filter items based on search term with debouncing
  const filterItems = useCallback((searchValue) => {
    if (!searchValue.trim()) {
      // Show first page of items
      setFilteredItems(itemMasterData.slice(0, ITEMS_PER_PAGE));
      setCurrentPage(1);
      setHasMoreData(itemMasterData.length > ITEMS_PER_PAGE);
      return;
    }
    
    // Use Web Workers for heavy filtering to prevent UI freeze
    if (itemMasterData.length > 1000) {
      // For large datasets, use Web Worker
      const worker = new Worker(URL.createObjectURL(new Blob([`
        self.onmessage = function(e) {
          const { data, searchTerm } = e.data;
          const filtered = data.filter(item => 
            item.ITEM_NAME.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.ASICC_CODE.toLowerCase().includes(searchTerm.toLowerCase())
          );
          self.postMessage(filtered.slice(0, 50)); // Limit to 50 results
        };
      `])));
      
      worker.onmessage = function(e) {
        setFilteredItems(e.data);
        setCurrentPage(1);
        setHasMoreData(false); // No pagination for search results
      };
      
      worker.postMessage({ data: itemMasterData, searchTerm: searchValue });
      worker.terminate();
    } else {
      // For smaller datasets, filter directly
      const filtered = itemMasterData.filter(item => 
        item.ITEM_NAME.toLowerCase().includes(searchValue.toLowerCase()) ||
        item.ASICC_CODE.toLowerCase().includes(searchValue.toLowerCase())
      );
      
      setFilteredItems(filtered.slice(0, 50));
      setCurrentPage(1);
      setHasMoreData(false);
    }
  }, [itemMasterData]);
  
  // Filter block items based on search term with debouncing
  const filterBlockItems = useCallback((searchValue) => {
    if (!searchValue.trim()) {
      // Show first page of items
      setFilteredBlockItems(blockItemData.slice(0, ITEMS_PER_PAGE));
      setBlockItemCurrentPage(1);
      setHasMoreBlockItems(blockItemData.length > ITEMS_PER_PAGE);
      return;
    }
    
    // Use Web Workers for heavy filtering to prevent UI freeze
    if (blockItemData.length > 1000) {
      // For large datasets, use Web Worker
      const worker = new Worker(URL.createObjectURL(new Blob([`
        self.onmessage = function(e) {
          const { data, searchTerm } = e.data;
          const filtered = data.filter(item => 
            (item.item_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (item.block_item || '').toLowerCase().includes(searchTerm.toLowerCase())
          );
          self.postMessage(filtered.slice(0, 50)); // Limit to 50 results
        };
      `])));
      
      worker.onmessage = function(e) {
        setFilteredBlockItems(e.data);
        setBlockItemCurrentPage(1);
        setHasMoreBlockItems(false); // No pagination for search results
      };
      
      worker.postMessage({ data: blockItemData, searchTerm: searchValue });
      worker.terminate();
    } else {
      // For smaller datasets, filter directly
      const filtered = blockItemData.filter(item => 
        (item.item_name || '').toLowerCase().includes(searchValue.toLowerCase()) ||
        (item.block_item || '').toLowerCase().includes(searchValue.toLowerCase())
      );
      
      setFilteredBlockItems(filtered.slice(0, 50));
      setBlockItemCurrentPage(1);
      setHasMoreBlockItems(false);
    }
  }, [blockItemData]);
  
  // Debounced search
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId;
      return (searchValue) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          setSearchTerm(searchValue);
          filterItems(searchValue);
        }, 300);
      };
    })(),
    [filterItems]
  );
  
  // Debounced search for block items
  const debouncedBlockItemSearch = useCallback(
    (() => {
      let timeoutId;
      return (searchValue) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          setBlockItemSearchTerm(searchValue);
          filterBlockItems(searchValue);
        }, 300);
      };
    })(),
    [filterBlockItems]
  );
  
  // Clear search and reset to paginated view
  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setFilteredItems(itemMasterData.slice(0, ITEMS_PER_PAGE));
    setCurrentPage(1);
    setHasMoreData(itemMasterData.length > ITEMS_PER_PAGE);
  }, [itemMasterData]);
  
  // Clear block item search and reset to paginated view
  const clearBlockItemSearch = useCallback(() => {
    setBlockItemSearchTerm('');
    setFilteredBlockItems(blockItemData.slice(0, ITEMS_PER_PAGE));
    setBlockItemCurrentPage(1);
    setHasMoreBlockItems(blockItemData.length > ITEMS_PER_PAGE);
  }, [blockItemData]);
  
  // Fetch unit data by item code
  // const fetchUnitByItemCode = useCallback(async (itemCode) => {
  //   if (!itemCode) return null;
    
  //   try {
  //     const currentConfig = getAuthConfig();
  //     if (!currentConfig) {
  //       throw new Error('Authentication token not found');
  //     }

  //     console.log('Fetching unit data for item code:', itemCode);
  //     const newObject = {
  //       asicc_code: itemCode
  //     };
      
  //     const response = await axios.post(`${API_URL}api/asi/blockHFetchDataByItemCodeNPCMS`, newObject, currentConfig);
      
  //     if (response.data && response.data.status && response.data.data && response.data.data.blockHData) {
  //       const unitData = response.data.data.blockHData;
  //       console.log('Unit data received:', unitData);
  //       return unitData;
  //     } else {
  //       console.log('No unit data found for item code:', itemCode);
  //       return null;
  //     }
  //   } catch (error) {
  //     console.error('Error fetching unit data:', error);
      
  //     if (error.response?.status === 403) {
  //       toast.error('Access denied (403). Please check your permissions.');
  //     } else if (error.response?.status === 401) {
  //       toast.error('Unauthorized (401). Please login again.');
  //     } else if (error.response?.status === 404) {
  //       console.log('No data found for item code:', itemCode);
  //       return null;
  //     } else {
  //       toast.error('Error fetching unit data. Please try again.');
  //     }
  //     return null;
  //   }
  // }, []);
  
  // Initialize data on mount - only call once
  useEffect(() => {
    // Only fetch if we don't have data and no API call is in progress
    if (!itemMasterData.length && !isApiCallInProgress) {
      fetchItemMasterData();
    }
    // Also fetch block item data
    if (!blockItemData.length && !isBlockItemApiCallInProgress) {
      fetchBlockItemData();
    }
  }, []); // Empty dependency array to ensure it only runs once
  
  // Cleanup function
  useEffect(() => {
    return () => {
      // Cleanup if needed
    };
  }, []);

  return {
    // Data
    itemMasterData,
    filteredItems,
    
    // Loading states
    isLoading,
    isLoadingMore,
    
    // Pagination
    currentPage,
    hasMoreData,
    loadMoreData,
    
    // Search
    searchTerm,
    debouncedSearch,
    clearSearch,
    
    // Actions
    fetchItemMasterData,
    refreshData: () => fetchItemMasterData(true),
    // fetchUnitByItemCode,
    
    // Error
    error,
    
    // Cache info
    isCacheValid,
    cacheTimestamp: globalCacheTimestamp,
    
    // Balance Sheet Block Item Data
    blockItemData,
    filteredBlockItems,
    
    // Block Item Loading states
    isLoadingBlockItems,
    isLoadingMoreBlockItems,
    
    // Block Item Pagination
    blockItemCurrentPage,
    hasMoreBlockItems,
    loadMoreBlockItemData,
    
    // Block Item Search
    blockItemSearchTerm,
    debouncedBlockItemSearch,
    clearBlockItemSearch,
    
    // Block Item Actions
    fetchBlockItemData,
    refreshBlockItemData: () => fetchBlockItemData(true),
    
    // Block Item Error
    blockItemError,
    
    // Block Item Cache info
    isBlockItemCacheValid,
    blockItemCacheTimestamp: globalBlockItemCacheTimestamp
  };
};

export default useASIItemMaster;
